import { redirect } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { assertAdminAccess, assertStaffAccess } from "@/lib/admin-access.functions";

/**
 * Route-level admin gate. Runs before the admin route renders (client-only,
 * because the parent `_authenticated` layout is `ssr: false`).
 *
 * The decision is made on the server: `assertAdminAccess` validates the bearer
 * token and re-reads the caller's roles inside the handler, so tampering with
 * client state cannot open the console. Row level security on every admin table
 * remains the authoritative backstop.
 */
export async function requireAdminRole() {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) throw redirect({ to: "/auth" });

  try {
    await assertAdminAccess();
  } catch {
    throw redirect({ to: "/dashboard" });
  }
  return { isAdmin: true as const };
}

/**
 * Console gate for staff: admins get everything, editors get the content area
 * only. Verified server-side, then enforced again by RLS and the publish
 * permission trigger.
 */
export async function requireConsoleAccess() {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) throw redirect({ to: "/auth" });

  try {
    const access = await assertStaffAccess();
    return { isAdmin: access.isAdmin, isEditor: access.isEditor };
  } catch {
    throw redirect({ to: "/dashboard" });
  }
}
